import { Component, ViewChild, inject, signal } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { map } from "rxjs/operators";
import { SoapTemplateBackend } from "src/app/backend/api/soap-template.backend";
import { SoapTemplate } from "src/app/backend/models/soap-template";
import { sntConfirm } from "src/app/common/dialog";
import { PopupService } from "src/app/services/popup/popup.service";
import { SseEvents } from "src/app/sse/sse-events";
import { sntTranslate } from "src/app/support/translate";
import { ButtonClickAsyncEvent } from "src/app/components/ui/button/button-click-async-event";
import {
  SseComponent,
  SSERegister,
  SSESetupReturnType
} from "src/app/sse/sse-component";
import { forkJoin, lastValueFrom } from "rxjs";
import {
  ContainerBaseComponent,
  ContainerLoadFn
} from "src/app/components/ui/container/container-base/container-base.component";
import { routes } from "src/app/routing/routes";
import { TabService } from "src/app/services/tab.service";

type LoadData = {
  templates: SoapTemplate[];
};

@Component({
  selector: "pms-soap-template-master",
  templateUrl: "soap-template-master.component.html",
  styles: [
    `
      :host {
        display: flex;
        flex: 1;
      }
    `
  ],
  standalone: false
})
export class SoapTemplateMaster extends SseComponent {
  @ViewChild(ContainerBaseComponent)
  containerComponent?: ContainerBaseComponent;

  templates = signal<SoapTemplate[]>([]);

  loadFn: ContainerLoadFn<LoadData>;

  private readonly popupService = inject(PopupService);
  private readonly tabService = inject(TabService);
  private readonly soapTemplateBackend = inject(SoapTemplateBackend);

  constructor() {
    super();

    this.registerOnPageVisible(() => this.containerComponent?.refreshSilent());
    this.loadFn = () =>
      forkJoin({
        templates: this.soapTemplateBackend.listAll()
      });
  }

  setupSSEHandlers(): SSESetupReturnType {
    return [
      [
        [
          SseEvents.HEALTHCARE_HEALTH_RECORD_TEMPLATE_CREATED,
          SseEvents.HEALTHCARE_HEALTH_RECORD_TEMPLATE_UPDATED,
          SseEvents.HEALTHCARE_HEALTH_RECORD_TEMPLATE_CLONED,
          SseEvents.HEALTHCARE_HEALTH_RECORD_TEMPLATE_DELETED
        ],
        async () => {
          await this.containerComponent?.refreshSilent();
        }
      ]
    ];
  }

  onData(loadData: LoadData): void {
    this.templates.set(loadData.templates);
  }

  async onNew(): Promise<void> {
    await this.popupService.createEntity<{ name: string }>({
      title: sntTranslate(marker("Nuovo template")),
      modalConfig: {
        dialog: true,
        height: 128
      },
      formItems: [
        {
          dataField: "name",
          validationRules: [{ type: "required" }],
          label: {
            text: sntTranslate(marker("Nome")),
            location: "top",
            showColon: false
          }
        }
      ],
      create: async (data) => {
        const id = await lastValueFrom(
          this.soapTemplateBackend
            .create({
              name: data.name,
              detail: {}
            })
            .pipe(map((soapTemplate) => soapTemplate.id))
        );

        await this.tabService.openAndCloseIfSuccessfull(
          routes.organization.soap_templates.id(id),
          routes.organization.soap_templates.list()
        );
      }
    });
  }

  async onEnable(
    buttonClickAsyncEvent: ButtonClickAsyncEvent,
    soapTemplate: SoapTemplate
  ): Promise<void> {
    await buttonClickAsyncEvent.execute(async () => {
      await lastValueFrom(this.soapTemplateBackend.enable(soapTemplate.id));
    });
  }

  async onDisable(
    buttonClickAsyncEvent: ButtonClickAsyncEvent,
    soapTemplate: SoapTemplate
  ): Promise<void> {
    await buttonClickAsyncEvent.execute(async () => {
      await lastValueFrom(this.soapTemplateBackend.disable(soapTemplate.id));
    });
  }

  async onPreview(
    buttonClickAsyncEvent: ButtonClickAsyncEvent,
    template: SoapTemplate
  ): Promise<void> {
    await buttonClickAsyncEvent.execute(async () => {
      await this.tabService.openAndCloseIfSuccessfull(
        routes.organization.soap_templates.id(template.id),
        routes.organization.soap_templates.list()
      );
    });
  }

  async onEdit(
    buttonClickAsyncEvent: ButtonClickAsyncEvent,
    template: SoapTemplate
  ): Promise<void> {
    await buttonClickAsyncEvent.execute(async () => {
      await this.tabService.openAndCloseIfSuccessfull(
        routes.organization.soap_templates.id(template.id),
        routes.organization.soap_templates.list()
      );
    });
  }

  async onClone(
    buttonClickAsyncEvent: ButtonClickAsyncEvent,
    template: SoapTemplate
  ): Promise<void> {
    await buttonClickAsyncEvent.execute(async () => {
      const duplicatedTemplate = await lastValueFrom(
        this.soapTemplateBackend.clone(template.id)
      );

      await this.tabService.openAndCloseIfSuccessfull(
        routes.organization.soap_templates.id(duplicatedTemplate.id),
        routes.organization.soap_templates.list()
      );
    });
  }

  async onDuplicate(
    buttonClickAsyncEvent: ButtonClickAsyncEvent,
    template: SoapTemplate
  ): Promise<void> {
    await buttonClickAsyncEvent.execute(async () => {
      const duplicatedTemplate = await lastValueFrom(
        this.soapTemplateBackend.duplicate(template.id)
      );

      await this.tabService.openAndCloseIfSuccessfull(
        routes.organization.soap_templates.id(duplicatedTemplate.id),
        routes.organization.soap_templates.list()
      );
    });
  }

  async onDelete(template: SoapTemplate): Promise<void> {
    const result = await sntConfirm(
      sntTranslate(marker("Modello SOAP")),
      sntTranslate(marker("Elminare il modello SOAP?"), {
        number: template.name
      })
    );

    if (result) {
      await lastValueFrom(this.soapTemplateBackend.delete(template.id));
    }
  }
}
